import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import {
  workspacesApi,
  businessContextResearchApi,
  type BusinessContextResearchJob,
} from '../lib/api';
import { BackLink, Spinner } from '@rankhelm/ui';
import type { Workspace } from '@rankhelm/shared';

export function BusinessContextPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    businessReach: '' as 'Local' | 'Regional' | 'National' | 'Global' | '',
    businessType: '' as 'B2B' | 'B2C' | 'Both' | '',
    serviceArea: '',
    primaryServices: [] as string[],
    idealCustomerProfile: '',
    knownCompetitors: [] as { name: string; url: string }[],
    marketingChannels: [] as string[],
    contentGoals: [] as string[],
    whatMakesYouDifferent: '',
    additionalContext: '',
  });

  const [newService, setNewService] = useState('');
  const [newCompetitorName, setNewCompetitorName] = useState('');
  const [newCompetitorUrl, setNewCompetitorUrl] = useState('');

  // --- Research & Pre-populate state ---
  // The intro screen is shown only when the workspace has no context yet.
  // Once the user clicks the "Research and Pre-populate" button we enqueue a
  // queue job and poll every 5s until it completes or fails.
  const [hasExistingContext, setHasExistingContext] = useState(false);
  const [researchJob, setResearchJob] = useState<BusinessContextResearchJob | null>(null);
  const [researchStarting, setResearchStarting] = useState(false);
  const [researchError, setResearchError] = useState('');
  const [sources, setSources] = useState<string[]>([]);
  const pollingRef = useRef<number | null>(null);

  // Controls the "Unlock and Regenerate" confirmation dialog. Shown before
  // we re-queue a research job so the user has a clear chance to back out —
  // running research overwrites the current in-memory form with the new AI
  // draft (saved context stays intact until they click Save).
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  const applyDraftToForm = (draft: NonNullable<BusinessContextResearchJob['result']>) => {
    setForm({
      businessReach: draft.businessReach || '',
      businessType: draft.businessType || '',
      serviceArea: draft.serviceArea || '',
      primaryServices: draft.primaryServices || [],
      idealCustomerProfile: draft.idealCustomerProfile || '',
      knownCompetitors: draft.knownCompetitors || [],
      marketingChannels: draft.marketingChannels || [],
      contentGoals: draft.contentGoals || [],
      whatMakesYouDifferent: draft.whatMakesYouDifferent || '',
      additionalContext: draft.additionalContext || '',
    });
  };

  // Load workspace + existing business context + latest research job.
  useEffect(() => {
    async function load() {
      if (!workspaceId) return;
      try {
        const res = await workspacesApi.get(workspaceId);
        const w = res.data;
        setWorkspace(w);

        let contextFilled = false;
        if (w.business_context_json && w.business_context_json.trim() !== '') {
          try {
            const context = JSON.parse(w.business_context_json);
            setForm({
              businessReach: context.businessReach || '',
              businessType: context.businessType || '',
              serviceArea: context.serviceArea || '',
              primaryServices: context.primaryServices || [],
              idealCustomerProfile: context.idealCustomerProfile || '',
              knownCompetitors: context.knownCompetitors || [],
              marketingChannels: context.marketingChannels || [],
              contentGoals: context.contentGoals || [],
              whatMakesYouDifferent: context.whatMakesYouDifferent || '',
              additionalContext: context.additionalContext || '',
            });
            contextFilled = true;
          } catch (e) {
            console.error('Failed to parse business context:', e);
          }
        }
        setHasExistingContext(contextFilled);

        // If a research job exists, restore it so polling can resume.
        try {
          const jobRes = await businessContextResearchApi.latest(workspaceId);
          if (jobRes.data) {
            setResearchJob(jobRes.data);
            if (jobRes.data.sources) setSources(jobRes.data.sources);
            if (jobRes.data.status === 'completed' && jobRes.data.result && !contextFilled) {
              applyDraftToForm(jobRes.data.result);
            }
          }
        } catch (e) {
          // Research may fail to load (e.g. missing route on older deploys) — non-fatal.
          console.warn('Failed to load research job state:', e);
        }
      } catch (e) {
        console.error('Failed to load workspace:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [workspaceId]);

  // Poll the research job every 5s while it is pending/running. Stop on
  // completion or failure.
  useEffect(() => {
    if (!workspaceId || !researchJob) return;
    if (researchJob.status !== 'pending' && researchJob.status !== 'researching' && researchJob.status !== 'normalizing') {
      return;
    }

    const tick = async () => {
      try {
        const res = await businessContextResearchApi.get(workspaceId, researchJob.id);
        const next = res.data;
        setResearchJob(next);
        if (next.sources) setSources(next.sources);
        if (next.status === 'completed' && next.result) {
          applyDraftToForm(next.result);
        }
      } catch (e) {
        console.error('Research poll failed:', e);
      }
    };
    pollingRef.current = window.setInterval(tick, 5000);
    return () => {
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [workspaceId, researchJob?.id, researchJob?.status]);

  const handleStartResearch = async () => {
    if (!workspaceId) return;
    setResearchError('');
    setResearchStarting(true);
    try {
      const res = await businessContextResearchApi.start(workspaceId);
      // Fetch the full job record so we know the initial status text.
      const jobRes = await businessContextResearchApi.get(workspaceId, res.data.jobId);
      setResearchJob(jobRes.data);
      if (jobRes.data.sources) setSources(jobRes.data.sources);
      // The POST dedupes on in-flight jobs, so `start()` may return an already
      // completed job if it finished between previous POST and this GET. The
      // polling effect bails out for terminal statuses, so apply the draft
      // here synchronously instead of waiting for the first poll tick.
      if (jobRes.data.status === 'completed' && jobRes.data.result) {
        applyDraftToForm(jobRes.data.result);
      }
    } catch (err) {
      setResearchError(err instanceof Error ? err.message : 'Failed to start research');
    } finally {
      setResearchStarting(false);
    }
  };

  const handleConfirmRegenerate = async () => {
    setShowRegenerateConfirm(false);
    await handleStartResearch();
  };

  const canRunResearch = !!(workspace?.linkedin_url || workspace?.website_url);
  const researchInProgress =
    researchJob?.id !== 'skip' &&
    (researchJob?.status === 'pending' ||
      researchJob?.status === 'researching' ||
      researchJob?.status === 'normalizing');
  // The regenerate button is available any time the form is visible and no
  // research job is currently running — i.e. the user has either saved context
  // already, skipped the intro, or had a previous run complete/fail.
  const showRegenerateButton =
    (hasExistingContext || !!researchJob) && !researchInProgress;
  const regenerateLabel =
    researchJob?.status === 'failed' && researchJob.id !== 'skip'
      ? 'Try AI research again'
      : 'Regenerate with AI';

  const handleAddService = () => {
    if (newService.trim() && form.primaryServices.length < 10) {
      setForm({
        ...form,
        primaryServices: [...form.primaryServices, newService.trim()],
      });
      setNewService('');
    }
  };

  const handleRemoveService = (index: number) => {
    setForm({
      ...form,
      primaryServices: form.primaryServices.filter((_, i) => i !== index),
    });
  };

  const handleAddCompetitor = () => {
    if (newCompetitorName.trim() && form.knownCompetitors.length < 10) {
      setForm({
        ...form,
        knownCompetitors: [
          ...form.knownCompetitors,
          { name: newCompetitorName.trim(), url: newCompetitorUrl.trim() },
        ],
      });
      setNewCompetitorName('');
      setNewCompetitorUrl('');
    }
  };

  const handleRemoveCompetitor = (index: number) => {
    setForm({
      ...form,
      knownCompetitors: form.knownCompetitors.filter((_, i) => i !== index),
    });
  };

  const handleChannelChange = (channel: string) => {
    if (form.marketingChannels.includes(channel)) {
      setForm({
        ...form,
        marketingChannels: form.marketingChannels.filter(c => c !== channel),
      });
    } else {
      setForm({
        ...form,
        marketingChannels: [...form.marketingChannels, channel],
      });
    }
  };

  const handleContentGoalChange = (goal: string) => {
    if (form.contentGoals.includes(goal)) {
      setForm({
        ...form,
        contentGoals: form.contentGoals.filter(g => g !== goal),
      });
    } else {
      setForm({
        ...form,
        contentGoals: [...form.contentGoals, goal],
      });
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    // Validation
    if (!form.businessReach) {
      setError('Business Reach is required');
      setSaving(false);
      return;
    }
    if (!form.businessType) {
      setError('Business Type is required');
      setSaving(false);
      return;
    }
    if (form.primaryServices.length === 0) {
      setError('Please add at least 1 primary service');
      setSaving(false);
      return;
    }
    if (form.contentGoals.length === 0) {
      setError('Please select at least 1 content goal');
      setSaving(false);
      return;
    }

    try {
      await workspacesApi.update(workspaceId!, {
        business_context_json: JSON.stringify(form),
      });
      setMessage('Business context saved successfully!');
      setTimeout(() => {
        navigate(`/workspaces/${workspaceId}`);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save business context');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner message="Loading workspace..." fullPage />;
  }

  if (!workspace) {
    return <div className="text-red-400">Workspace not found</div>;
  }

  const marketingChannelOptions = [
    'Organic Search',
    'Paid Search',
    'Social Media',
    'Email',
    'Referral',
    'Content Marketing',
    'Events',
    'Other',
  ];

  const contentGoalOptions = [
    'Brand Authority',
    'Lead Generation',
    'Organic Traffic',
    'Thought Leadership',
    'Local SEO',
    'Customer Education',
  ];

  return (
    <div>
      <BackLink to={`/workspaces/${workspaceId}`} label="Back to Workspace" />

      {message && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 text-sm rounded-lg px-4 py-3 mb-6">
          {message}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg px-4 py-3 mb-6">
          {error}
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Business Context</h1>
          <p className="text-slate-400">
            Help us understand your business, market, and content strategy so we can deliver better intelligence reports and content recommendations.
          </p>
        </div>
        {showRegenerateButton && (
          <button
            type="button"
            onClick={() => {
              setResearchError('');
              setShowRegenerateConfirm(true);
            }}
            disabled={researchStarting || !canRunResearch}
            title={!canRunResearch ? 'Add a website or LinkedIn URL to the workspace first' : undefined}
            className="shrink-0 px-4 py-2 bg-navy-800 hover:bg-navy-700 disabled:bg-navy-900 disabled:text-slate-500 text-slate-200 text-sm font-medium rounded-lg border border-navy-600 hover:border-helm-500/50 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {researchStarting ? 'Starting…' : regenerateLabel}
          </button>
        )}
      </div>

      {/*
        Research & Pre-populate intro screen.
        Shown only when the workspace has no saved business context AND no
        research job has been started yet. Once the user clicks the button,
        we enqueue the job and swap to the form with a progress banner.
      */}
      {!hasExistingContext && !researchJob && (
        <div className="bg-navy-900 border border-helm-500/30 rounded-xl p-8 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-helm-500/20 border border-helm-500/40 flex items-center justify-center text-helm-300 shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-white mb-2">Research and Pre-populate</h2>
              <p className="text-slate-300 text-sm mb-5">
                We&apos;ll populate the information about your business using our neural engine,
                and then you&apos;ll have an opportunity to edit it and optimize it. Click the
                button below to get started. This process may take several minutes.
              </p>
              {!workspace.linkedin_url && !workspace.website_url && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-sm rounded-lg px-4 py-3 mb-4">
                  Add a website or LinkedIn URL to the workspace before running research.{' '}
                  <Link to={`/workspaces/${workspaceId}`} className="underline">Update workspace settings →</Link>
                </div>
              )}
              {researchError && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg px-4 py-3 mb-4">
                  {researchError}
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleStartResearch}
                  disabled={researchStarting || (!workspace.linkedin_url && !workspace.website_url)}
                  className="px-5 py-2.5 bg-helm-500 hover:bg-helm-400 disabled:bg-navy-700 disabled:text-slate-500 text-white font-medium rounded-lg transition-colors"
                >
                  {researchStarting ? 'Starting…' : 'Research and Pre-populate'}
                </button>
                <button
                  type="button"
                  onClick={() => setResearchJob({
                    id: 'skip',
                    status: 'failed',
                    progress_message: null,
                    result: null,
                    sources: [],
                    error: null,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                  })}
                  className="px-5 py-2.5 bg-transparent border border-navy-600 hover:border-navy-500 text-slate-300 font-medium rounded-lg transition-colors"
                >
                  Skip and fill manually
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Research progress banner */}
      {researchJob && researchJob.id !== 'skip' && (researchJob.status === 'pending' || researchJob.status === 'researching' || researchJob.status === 'normalizing') && (
        <div className="bg-navy-900 border border-helm-500/30 rounded-xl p-5 mb-8 flex items-center gap-4">
          <Spinner />
          <div className="flex-1">
            <div className="text-white font-medium mb-1">Researching your business…</div>
            <div className="text-slate-400 text-sm">
              {researchJob.progress_message ?? 'This may take several minutes. You can leave this page and come back — we&apos;ll keep working in the background.'}
            </div>
          </div>
        </div>
      )}

      {researchJob && researchJob.id !== 'skip' && researchJob.status === 'completed' && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-300 text-sm rounded-lg px-4 py-3 mb-8">
          Research complete. Review the pre-populated fields below, edit anything that looks off, then save.
        </div>
      )}

      {researchJob && researchJob.id !== 'skip' && researchJob.status === 'failed' && researchJob.error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg px-4 py-3 mb-8">
          {researchJob.error} You can click <span className="font-medium">Try AI research again</span> above, or fill the form manually below.
        </div>
      )}
      {researchError && (hasExistingContext || researchJob) && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg px-4 py-3 mb-8">
          {researchError}
        </div>
      )}

      {/* Confirmation dialog for Unlock / Regenerate */}
      {showRegenerateConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="regenerate-confirm-title"
        >
          <div className="w-full max-w-md bg-navy-900 border border-navy-700 rounded-xl p-6 shadow-xl">
            <h3 id="regenerate-confirm-title" className="text-lg font-semibold text-white mb-2">
              Regenerate with AI?
            </h3>
            <p className="text-slate-300 text-sm mb-2">
              This will run a new AI research pass and overwrite the fields currently in this form with a fresh draft.
            </p>
            <p className="text-slate-400 text-sm mb-5">
              Your saved business context stays intact until you click <span className="text-white">Save</span>. Click <span className="text-white">Cancel</span> to keep your current values.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowRegenerateConfirm(false)}
                className="px-4 py-2 bg-transparent border border-navy-600 hover:border-navy-500 text-slate-300 text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRegenerate}
                className="px-4 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Yes, regenerate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hide the form while the intro screen is active — the form appears
          once research is started (or skipped). */}
      {(hasExistingContext || researchJob) && (
      <form onSubmit={handleSave} className="space-y-8">
        {/* Business Reach */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
          <label className="block text-white font-medium mb-3">Business Reach</label>
          <p className="text-slate-400 text-sm mb-4">Where do you primarily operate?</p>
          <div className="space-y-2">
            {['Local', 'Regional', 'National', 'Global'].map((option) => (
              <label key={option} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="businessReach"
                  value={option}
                  checked={form.businessReach === option}
                  onChange={(e) => setForm({ ...form, businessReach: e.target.value as typeof form.businessReach })}
                  className="w-4 h-4 accent-helm-500"
                />
                <span className="text-slate-300">{option}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Business Type */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
          <label className="block text-white font-medium mb-3">Business Type</label>
          <p className="text-slate-400 text-sm mb-4">How do you primarily serve clients?</p>
          <div className="space-y-2">
            {['B2B', 'B2C', 'Both'].map((option) => (
              <label key={option} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="businessType"
                  value={option}
                  checked={form.businessType === option}
                  onChange={(e) => setForm({ ...form, businessType: e.target.value as typeof form.businessType })}
                  className="w-4 h-4 accent-helm-500"
                />
                <span className="text-slate-300">{option}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Service Area / Target Markets */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
          <label className="block text-white font-medium mb-2">Service Area / Target Markets</label>
          <p className="text-slate-400 text-sm mb-4">
            Example: US-wide, primarily tech startups and mid-market SaaS
          </p>
          <textarea
            value={form.serviceArea}
            onChange={(e) => setForm({ ...form, serviceArea: e.target.value })}
            placeholder="Describe your geographic and market reach..."
            rows={3}
            className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 text-sm"
          />
        </div>

        {/* Primary Services */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
          <label className="block text-white font-medium mb-2">Primary Services</label>
          <p className="text-slate-400 text-sm mb-4">
            Add 1-10 core services you offer (min 1 required)
          </p>
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newService}
              onChange={(e) => setNewService(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddService();
                }
              }}
              placeholder="e.g., SEO Consulting"
              className="flex-1 px-4 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 text-sm"
            />
            <button
              type="button"
              onClick={handleAddService}
              disabled={form.primaryServices.length >= 10 || !newService.trim()}
              className="px-4 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {form.primaryServices.map((service, idx) => (
              <div
                key={idx}
                className="bg-helm-500/20 border border-helm-500/50 text-helm-300 px-3 py-1 rounded-full text-sm flex items-center gap-2"
              >
                {service}
                <button
                  type="button"
                  onClick={() => handleRemoveService(idx)}
                  className="ml-1 hover:text-helm-200"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Ideal Customer Profile */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
          <label className="block text-white font-medium mb-2">Ideal Customer Profile (ICP)</label>
          <p className="text-slate-400 text-sm mb-4">
            Example: Mid-market B2B SaaS companies, $5M-$50M ARR, looking to scale organic traffic
          </p>
          <textarea
            value={form.idealCustomerProfile}
            onChange={(e) => setForm({ ...form, idealCustomerProfile: e.target.value })}
            placeholder="Describe your ideal customer in detail..."
            rows={4}
            className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 text-sm"
          />
        </div>

        {/* Known Competitors */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
          <label className="block text-white font-medium mb-2">Known Competitors</label>
          <p className="text-slate-400 text-sm mb-4">Add up to 10 competitors (name and optional URL)</p>
          <div className="space-y-2 mb-4">
            <input
              type="text"
              value={newCompetitorName}
              onChange={(e) => setNewCompetitorName(e.target.value)}
              placeholder="Competitor name"
              className="w-full px-4 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 text-sm"
            />
            <div className="flex gap-2">
              <input
                type="url"
                value={newCompetitorUrl}
                onChange={(e) => setNewCompetitorUrl(e.target.value)}
                placeholder="https://competitor.com (optional)"
                className="flex-1 px-4 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 text-sm"
              />
              <button
                type="button"
                onClick={handleAddCompetitor}
                disabled={form.knownCompetitors.length >= 10 || !newCompetitorName.trim()}
                className="px-4 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {form.knownCompetitors.map((comp, idx) => (
              <div key={idx} className="flex items-center justify-between bg-navy-800 p-3 rounded-lg">
                <div>
                  <p className="text-white text-sm font-medium">{comp.name}</p>
                  {comp.url && (
                    <a
                      href={comp.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-helm-400 hover:text-helm-300 text-xs"
                    >
                      {comp.url}
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveCompetitor(idx)}
                  className="text-slate-400 hover:text-red-400 text-sm"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Marketing Channels */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
          <label className="block text-white font-medium mb-3">Current Marketing Channels</label>
          <p className="text-slate-400 text-sm mb-4">Which channels are you currently using?</p>
          <div className="space-y-2">
            {marketingChannelOptions.map((channel) => (
              <label key={channel} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.marketingChannels.includes(channel)}
                  onChange={() => handleChannelChange(channel)}
                  className="w-4 h-4 accent-helm-500"
                />
                <span className="text-slate-300">{channel}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Content Goals */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
          <label className="block text-white font-medium mb-3">Content Goals</label>
          <p className="text-slate-400 text-sm mb-4">Select at least 1 goal for your content strategy</p>
          <div className="space-y-2">
            {contentGoalOptions.map((goal) => (
              <label key={goal} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.contentGoals.includes(goal)}
                  onChange={() => handleContentGoalChange(goal)}
                  className="w-4 h-4 accent-helm-500"
                />
                <span className="text-slate-300">{goal}</span>
              </label>
            ))}
          </div>
        </div>

        {/* What Makes You Different */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
          <label className="block text-white font-medium mb-2">What Makes You Different?</label>
          <p className="text-slate-400 text-sm mb-4">
            Describe your unique value proposition and competitive advantages
          </p>
          <textarea
            value={form.whatMakesYouDifferent}
            onChange={(e) => setForm({ ...form, whatMakesYouDifferent: e.target.value })}
            placeholder="What sets you apart from competitors?..."
            rows={4}
            className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 text-sm"
          />
        </div>

        {/* Additional Context */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
          <label className="block text-white font-medium mb-2">Additional Context</label>
          <p className="text-slate-400 text-sm mb-4">Optional: Any other information we should know?</p>
          <textarea
            value={form.additionalContext}
            onChange={(e) => setForm({ ...form, additionalContext: e.target.value })}
            placeholder="Share any additional context about your business, market challenges, or goals..."
            rows={3}
            className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 text-sm"
          />
        </div>

        {/* Sources panel — populated once Perplexity research completes. */}
        {sources.length > 0 && (
          <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
            <label className="block text-white font-medium mb-2">Research Sources</label>
            <p className="text-slate-400 text-sm mb-4">
              Citations used by the AI to pre-populate this form.
            </p>
            <ul className="space-y-1.5">
              {sources.map((src, i) => (
                <li key={`${src}-${i}`} className="text-sm">
                  <a
                    href={src}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-helm-400 hover:text-helm-300 break-all underline"
                  >
                    {src}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex items-center gap-3 pt-6 border-t border-navy-700">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-helm-500 hover:bg-helm-400 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Business Context'}
          </button>
          <Link
            to={`/workspaces/${workspaceId}`}
            className="px-6 py-3 bg-navy-800 hover:bg-navy-700 text-slate-300 font-medium rounded-lg border border-navy-600 transition-colors"
          >
            Cancel
          </Link>
        </div>
      </form>
      )}
    </div>
  );
}
